import { useCallback, useEffect, useMemo, useState } from 'react';

import { AuthContextType, useAuth } from '@auth/AuthProvider';
import LaunchpadController from '@data/controller/LaunchpadController';
import {
  Launchpad,
  LaunchpadLink,
  launchpadFromResponse,
  normalizeLaunchpadAttributes,
} from '@interfaces/launchpad.interface';

/// A link with a stable identity, so rows keep their React key while the name,
/// URL, and position all change underneath.
export interface EditableLaunchpadLink extends LaunchpadLink {
  id: string;
}

export interface LaunchpadLinksEditor {
  links: EditableLaunchpadLink[];
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  error: string | null;
  addLink: () => void;
  updateLink: (linkID: string, changes: Partial<LaunchpadLink>) => void;
  removeLink: (linkID: string) => void;
  moveLink: (draggedLinkID: string, targetLinkID: string) => void;
  save: () => Promise<void>;
  reset: () => void;
}

// Owns everything about editing a Launchpad's links: the draft, whether it
// differs from what's saved, and the save itself. The page reads `links` for
// the live preview and hands the rest straight to the editor.
export default function useLaunchpadLinks(
  launchpad: Launchpad | null,
  onSaved: (launchpad: Launchpad) => void
): LaunchpadLinksEditor {
  const auth = useAuth();
  const savedLinks = launchpad?.metadata.attributes.links;

  const [links, setLinks] = useState<EditableLaunchpadLink[]>(() => toEditableLinks(savedLinks));
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The launchpad arrives after mount on a refresh, and is replaced again by a
  // save or an edit, so the draft follows whatever the server last confirmed.
  useEffect(() => {
    setLinks(toEditableLinks(savedLinks));
    setError(null);
  }, [savedLinks]);

  const hasUnsavedChanges = useMemo(() => {
    return !areLinksEqual(links, savedLinks);
  }, [links, savedLinks]);

  const addLink = useCallback(() => {
    setLinks((current) => withSortIndexes([
      ...current,
      { id: createLinkID(), name: '', url: '', sortIndex: current.length }
    ]));
  }, []);

  const updateLink = useCallback((linkID: string, changes: Partial<LaunchpadLink>) => {
    setLinks((current) => current.map((link) => link.id === linkID ? { ...link, ...changes } : link));
  }, []);

  const removeLink = useCallback((linkID: string) => {
    setLinks((current) => withSortIndexes(current.filter((link) => link.id !== linkID)));
  }, []);

  const moveLink = useCallback((draggedLinkID: string, targetLinkID: string) => {
    setLinks((current) => reorderLinks(current, draggedLinkID, targetLinkID));
  }, []);

  const reset = useCallback(() => {
    setError(null);
    setLinks(toEditableLinks(savedLinks));
  }, [savedLinks]);

  const save = useCallback(async () => {
    if (!launchpad) {
      return;
    }

    const result = await saveLinks(launchpad, links, auth);

    if (result.error) {
      setError(result.error);
      return;
    }

    setError(null);
    onSaved(result.launchpad!);
  }, [launchpad, links, auth, onSaved]);

  // The saving flag is set around the call rather than inside it so the async
  // helper stays free of component state.
  const saveWithState = useCallback(async () => {
    setIsSaving(true);

    try {
      await save();
    } finally {
      setIsSaving(false);
    }
  }, [save]);

  return {
    links,
    hasUnsavedChanges,
    isSaving,
    error,
    addLink,
    updateLink,
    removeLink,
    moveLink,
    save: saveWithState,
    reset
  };
}

async function saveLinks(
  launchpad: Launchpad,
  links: EditableLaunchpadLink[],
  auth: AuthContextType
): Promise<{ launchpad?: Launchpad; error?: string }> {
  if (!auth.churchId) {
    return { error: 'Missing church context. Please refresh and try again.' };
  }

  const trimmedLinks = links.map((link) => ({
    ...link,
    name: link.name.trim(),
    url: link.url.trim()
  }));

  if (trimmedLinks.some((link) => link.name.length === 0 || link.url.length === 0)) {
    return { error: 'Every link needs a name and a URL.' };
  }

  const attributes = normalizeLaunchpadAttributes({
    ...launchpad.metadata.attributes,
    links: trimmedLinks.map(({ id, ...link }) => link)
  });

  const response = await LaunchpadController.updateLaunchpad(
    auth.churchId,
    launchpad.launchpadID,
    attributes,
    auth
  );

  if (response.error) {
    return { error: response.error };
  }

  // The save went through, so fall back to what we sent if the response isn't
  // in a shape we can read.
  return {
    launchpad: launchpadFromResponse(response.body) ?? {
      ...launchpad,
      metadata: { ...launchpad.metadata, attributes }
    }
  };
}

export function toEditableLinks(links?: LaunchpadLink[] | null): EditableLaunchpadLink[] {
  return withSortIndexes((links ?? []).map((link) => ({ ...link, id: createLinkID() })));
}

// Position is what the API stores, so it's rewritten from array order after
// every structural change rather than tracked by hand.
function withSortIndexes(links: EditableLaunchpadLink[]): EditableLaunchpadLink[] {
  return links.map((link, index) => ({ ...link, sortIndex: index }));
}

function reorderLinks(
  links: EditableLaunchpadLink[],
  draggedLinkID: string,
  targetLinkID: string
): EditableLaunchpadLink[] {
  const draggedIndex = links.findIndex((link) => link.id === draggedLinkID);
  const targetIndex = links.findIndex((link) => link.id === targetLinkID);

  if (draggedIndex === -1 || targetIndex === -1) {
    return links;
  }

  const reordered = [...links];
  const [dragged] = reordered.splice(draggedIndex, 1);
  reordered.splice(targetIndex, 0, dragged);

  return withSortIndexes(reordered);
}

// Compares name, URL, and order -- reordering alone is a change worth saving.
function areLinksEqual(links: EditableLaunchpadLink[], savedLinks?: LaunchpadLink[] | null) {
  const saved = savedLinks ?? [];

  if (saved.length !== links.length) {
    return false;
  }

  return links.every((link, index) => {
    return link.name.trim() === saved[index].name && link.url.trim() === saved[index].url;
  });
}

let linkIDCounter = 0;

function createLinkID(): string {
  linkIDCounter += 1;

  return `link-${linkIDCounter}`;
}
